import React, { useEffect, useMemo, useCallback, useRef, useState } from "react";
import PropTypes from "prop-types";
import MaskedInput from "react-text-mask"
import createNumberMask from "text-mask-addons/dist/createNumberMask"
import Flatpickr from "react-flatpickr";
import { toast } from "react-toastify";
import { Button, Checkbox, CheckboxGroup, Drawer, Radio, RadioGroup, Select, Tabs, TextInput, TimeField } from "@opendate-app/setlist";
import { LockUnlocked01, Tag01 } from "@untitled-ui/icons-react";

import CurrencyInput from "../../CurrencyInput/components/CurrencyInput";
import { promoCodeMask } from "../../shared/promoCodeValidation";
import { offsetDateForBrowser, offsetDateForServer } from "../../../shared/timeZoneLogic";
import { parseDateTime, parseFloatFromString, showActivityOption, timeOnDate } from "./ticketTypeFormHelpers";
import PromoCodeTypeBadges, { promoCodeAppliesDiscount } from "./PromoCodeTypeBadges";

import "./TicketDrawer.css";

const axios = require("axios").default;

const createPromoCode = (csrfToken, team, confirm, promoCode, onSuccess) => {
  axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
  return axios.post("/teams/" + team.id + "/promo_codes", promoCodeParams(promoCode, confirm))
    .then(({ data }) => {
      onSuccess();
    })
    .catch((error) => {
      const messages = error?.response?.data;
      toast.error(Array.isArray(messages) ? messages.join(", ") : "Something went wrong. Try again.", {
        position: toast.POSITION.TOP_CENTER,
        draggable: false,
        closeOnClick: false,
        autoClose: 5000,
        hideProgressBar: true
      });
    });
};

const PROMO_CODE_FORM_ID = "promo-code-form";

const AVAILABILITY_OPTIONS = [
  { value: "Based on show details", label: "Based on show details" },
  { value: "Date & Time", label: "Date & Time" },
];

const CODE_TYPE_ITEMS = [
  {
    value: "reveal",
    label: "Reveal hidden tickets",
    supportingText: "Unlock presale or private tickets for fans with the code.",
    icon: LockUnlocked01
  },
  {
    value: "discount",
    label: "Apply a discount",
    supportingText: "Take a percent or dollar amount off at checkout.",
    icon: Tag01
  },
];

const DISCOUNT_MODE_ITEMS = [
  { value: "percentage", label: "% Percent" },
  { value: "amount", label: "$ Amount" },
];

const withDiscountChoice = (promoCode) => {
  const appliesDiscount = promoCodeAppliesDiscount({
    revealsHiddenTickets: promoCode.reveal_hidden_tickets,
    percentage: promoCode.percentage,
    amount: promoCode.amount_two_decimals
  });

  return {
    ...promoCode,
    applies_discount: appliesDiscount,
    limit_to_one_ticket_per_order: Boolean(promoCode.limit_to_one_ticket_per_order || promoCode.discounted_tickets_per_order),
    discount_mode: promoCode.amount_two_decimals ? "amount" : "percentage",
    ...(appliesDiscount ? {} : { amount_two_decimals: null, percentage: null })
  };
};

const SectionTitle = ({ icon: Icon, hint, children }) => (
  <div>
    <p className="ticket-drawer-block-title mb-0" style={{display: "flex", alignItems: "center", gap: "6px"}}>
      {Icon ? <Icon width={16} height={16} /> : null}
      {children}
    </p>
    {hint ? <p className="ticket-drawer-muted-text mb-0">{hint}</p> : null}
  </div>
);

const RequiredLabel = ({ children }) => (
  <>
    {children}
    <span className="ticket-drawer-required">&nbsp;*</span>
  </>
);

const toTimeValue = (dateTime) => {
  const date = parseDateTime(dateTime);
  if(!date){
    return null;
  }

  return String(date.getHours()).padStart(2, "0") + ":" + String(date.getMinutes()).padStart(2, "0");
};

const fromTimeValue = (value) => {
  if(value === null || value === undefined || value === ""){
    return "";
  }

  const [hours, minutes] = typeof(value) === "number"
    ? [Math.floor(value / 60), value % 60]
    : value.split(":").map(Number);
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date;
};

const sameDay = (a, b) => {
  const first = parseDateTime(a);
  const second = parseDateTime(b);
  return Boolean(first && second && first.toDateString() === second.toDateString());
};

const updatePromoCode = (csrfToken, team, confirm, promoCode, onSuccess) => {
  axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
  return axios.patch("/teams/" + team.id + "/promo_codes/" + promoCode.id, promoCodeParams(promoCode, confirm))
    .then(({ data }) => {
      onSuccess();
    })
    .catch((error) => {
      const messages = error?.response?.data;
      toast.error(Array.isArray(messages) ? messages.join(", ") : "Something went wrong. Try again.", {
        position: toast.POSITION.TOP_CENTER,
        draggable: false,
        closeOnClick: false,
        autoClose: 5000,
        hideProgressBar: true
      });
    });
};

const discountChoiceIsValid = (promoCode) => {
  if(!promoCode.reveal_hidden_tickets && !promoCode.applies_discount){
    return false;
  }

  if(!promoCode.applies_discount){
    return true;
  }

  var amount;
  if(promoCode.amount_two_decimals){
    amount = parseFloatFromString(promoCode.amount_two_decimals);
  }

  var percentage;
  if(promoCode.percentage){
    percentage = parseFloatFromString(promoCode.percentage);
  }

  return (
    (typeof(amount) === "number" && amount >= 0)
      || (typeof(percentage) === "number" && percentage >= 0 && percentage <= 100)
  );
};

const canSubmitPromoCodeForm = (promoCode) => {
  var startDate = parseDateTime(promoCode.start_date_midday);
  var startTime = timeOnDate(startDate, promoCode.start_time_with_wrong_date);
  var endDate   = parseDateTime(promoCode.end_date_midday);
  var endTime   = timeOnDate(endDate, promoCode.end_time_with_wrong_date);

  return (
    promoCode
      && promoCode.name
      && (promoCode.unlimited || promoCode.limit > 0)
      && ((
        promoCode.availability === "Date & Time"
        && startDate
        && startTime
        && endDate
        && endTime
        && (startDate <= endDate || sameDay(startDate, endDate))
        && startTime <= endTime
      ) || (
        promoCode.availability === "Based on show details"
        && promoCode.start_show_activity_id
        && promoCode.end_show_activity_id
      ))
  );
};

const promoCodeParams = (promoCode, confirm) => {
  var params = {
    promo_code: {
      calendar_event_id: confirm.id,
      name: promoCode.name,
      amount: (promoCode.amount_two_decimals ? parseFloatFromString(promoCode.amount_two_decimals) : null),
      percentage: (promoCode.percentage ? parseFloatFromString(promoCode.percentage) : null),
      limit: promoCode.limit,
      unlimited: promoCode ? promoCode.unlimited : false,
      reveal_hidden_tickets: promoCode.reveal_hidden_tickets,
      reveal_all_hidden_tickets: promoCode.reveal_all_hidden_tickets,
      should_be_default: promoCode.should_be_default,
      ticket_type_ids: (promoCode.ticket_type_ids ? promoCode.ticket_type_ids : []),
      discounts_only_selected_ticket_types: promoCode.discounts_only_selected_ticket_types,
      discounted_ticket_type_ids: (promoCode.discounted_ticket_type_ids ? promoCode.discounted_ticket_type_ids : []),
      event_hold_ids: (promoCode.event_hold_ids ? promoCode.event_hold_ids : []),
      general_admission_hold_ids: (promoCode.general_admission_hold_ids ? promoCode.general_admission_hold_ids : []),
      applies_to_ticket_types: promoCode.applies_to_ticket_types,
      applies_to_add_ons: promoCode.applies_to_add_ons,
      availability: promoCode.availability,
      start_date_midday: offsetDateForServer(promoCode.start_date_midday),
      start_time_with_wrong_date: offsetDateForServer(promoCode.start_time_with_wrong_date),
      end_date_midday: offsetDateForServer(promoCode.end_date_midday),
      end_time_with_wrong_date: offsetDateForServer(promoCode.end_time_with_wrong_date),
      start_show_activity_id: promoCode.start_show_activity_id,
      end_show_activity_id: promoCode.end_show_activity_id,
      limit_to_one_ticket_per_order: promoCode.limit_to_one_ticket_per_order,
      discounted_tickets_per_order: promoCode.limit_to_one_ticket_per_order ? (parseInt(promoCode.discounted_tickets_per_order, 10) || 1) : null,
      limit_to_one_add_on_per_order: promoCode.limit_to_one_add_on_per_order,
      uses_per_fan: parseInt(promoCode.uses_per_fan, 10) || null,
      applies_to_entire_order: promoCode.applies_to_entire_order,
      custom_js: promoCode.custom_js
    }
  }

  return params;
};

const customJsArgumentExample = () => {
return `{
  order_items: [
    { ticket_type_id: 1, quantity: 2 },
    { ticket_type_id: 2, quantity: 1 }
  ],
  add_on_order_items: [
    { add_on_id: 3, quantity: 1 }
  ]
}`
}

const customJsReturnExample = () => {
return `return {
  ticket_types: {
    1: 2
  },
  add_ons: {
    3: 1
  }
}`
}

const PromoCodeForm = ({
  currentUser,
  confirm,
  csrfToken,
  setIsOpen,
  isOpen,
  promoCodeToCopy,
  promoCodeToEdit,
  onClose,
  showActivities,
  team,
  ticketTypes,
  holds,
  fetchHolds,
  generalAdmissionHolds,
  fetchGeneralAdmissionHolds,
  initialPromoCode,
  onSubmitPromoCode,
  canSubmit,
  renderWindow,
  nameError,
  nameHint,
  saveAsDefaultProblem,
  discountOnly = false,
  availabilityOptions,
}) => {
  const [promoCode, setPromoCode] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);

  useEffect(() => {
    if(isOpen && initialPromoCode) {
      setPromoCode(withDiscountChoice(initialPromoCode));
    } else if(isOpen) {
      if (promoCodeToEdit.id) {
        axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
        axios.get("/teams/" + team.id + "/promo_codes/" + promoCodeToEdit.id + "/edit")
          .then(({ data }) => {
            setPromoCode(withDiscountChoice(Object.assign({}, data, {
              start_date_midday: offsetDateForBrowser(data.start_date_midday),
              start_time_with_wrong_date: offsetDateForBrowser(data.start_time_with_wrong_date),
              end_date_midday: offsetDateForBrowser(data.end_date_midday),
              end_time_with_wrong_date: offsetDateForBrowser(data.end_time_with_wrong_date)
            })))
          })
          .catch(err => {
            setPromoCode({});
          });
      } else {
        axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
        var url = "/teams/" + team.id + "/promo_codes/new?confirm_id=" + confirm.id;

        if(promoCodeToCopy){
          url = (url + "&copy_from_id=" + promoCodeToCopy.id);
        }

        axios.get(url)
          .then(({ data }) => {
            setPromoCode(withDiscountChoice(Object.assign({}, data, {
              start_date_midday: offsetDateForBrowser(data.start_date_midday),
              start_time_with_wrong_date: offsetDateForBrowser(data.start_time_with_wrong_date),
              end_date_midday: offsetDateForBrowser(data.end_date_midday),
              end_time_with_wrong_date: offsetDateForBrowser(data.end_time_with_wrong_date),
              unlimited: true
            })));
          })
          .catch(err => {
            setPromoCode({});
          });
      }
    }
  }, [isOpen, promoCodeToCopy, promoCodeToEdit, initialPromoCode])

  const admissionSeatingEventId = confirm.admission_seating_chart_event_id;

  useEffect(() => {
    if(isOpen && admissionSeatingEventId && fetchHolds){
      fetchHolds(csrfToken, { id: admissionSeatingEventId });
    }
  }, [isOpen, admissionSeatingEventId]);

  const holdsForEvent = useMemo(() => {
    return holds || [];
  }, [holds]);

  useEffect(() => {
    if(isOpen && confirm.id && fetchGeneralAdmissionHolds){
      fetchGeneralAdmissionHolds(csrfToken, confirm.id);
    }
  }, [isOpen, confirm.id]);

  const holdOptions = useMemo(() => {
    return [
      ...holdsForEvent.map(h => ({ value: `seat-${h.id}`, label: h.name })),
      ...(generalAdmissionHolds || []).map(h => ({ value: `ga-${h.id}`, label: `${h.name} (GA)` })),
    ];
  }, [holdsForEvent, generalAdmissionHolds]);

  const selectedHoldValues = [
    ...(promoCode.event_hold_ids || []).map(id => `seat-${id}`),
    ...(promoCode.general_admission_hold_ids || []).map(id => `ga-${id}`),
  ];

  const handleHoldsChange = (values) => {
    const idsWithPrefix = (prefix) => (values || [])
      .filter(value => value.startsWith(prefix))
      .map(value => value.slice(prefix.length))
      .map(id => (/^\d+$/.test(id) ? parseInt(id) : id));

    setPromoCode({
      ...promoCode,
      event_hold_ids: idsWithPrefix("seat-"),
      general_admission_hold_ids: idsWithPrefix("ga-"),
    });
  };


  const ticketTypeOptions = useMemo(() => {
    return (ticketTypes || [])
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(tt => ({ value: tt.id, label: tt.name }));
  }, [ticketTypes]);

  const showActivityOptions = useMemo(() => {
    return (showActivities || []).map(showActivity => showActivityOption(showActivity));
  }, [showActivities]);

  const handleIdsChange = useCallback((field) => (ids) => {
    setPromoCode({ ...promoCode, [field]: ids || [] });
  }, [promoCode]);

  const codeSampleStyle = {
    background: "var(--sl-bg-secondary)",
    border: "1px solid var(--sl-border-secondary)",
    borderRadius: "8px",
    color: "var(--sl-text-primary)",
    padding: "8px",
  };
  const warningTextStyle = { color: "var(--sl-text-warning)" };
  const errorTextStyle = { color: "var(--sl-text-error)" };
  const submittable = discountChoiceIsValid(promoCode)
    && (canSubmit ? canSubmit(promoCode) : canSubmitPromoCodeForm(promoCode));
  const defaultProblem = saveAsDefaultProblem && promoCode.should_be_default ? saveAsDefaultProblem(promoCode) : null;
  const nestedStyle = { marginLeft: "24px" };

  return (
    <Drawer
      open={isOpen}
      backdrop
      width={600}
      data-lift-conductor-orb={isOpen || undefined}
      title={promoCode.id ? "Edit Promo Code" : "Add Promo Code"}
      onClose={() => onClose()}
      footer={
        <>
          <span style={{display: "inline-flex", alignItems: "center", gap: "8px", marginRight: "auto"}}>
            <span className="ticket-drawer-muted-text">Shows as</span>
            <PromoCodeTypeBadges reveals={promoCode.reveal_hidden_tickets} discounts={promoCode.applies_discount} />
          </span>
          <Button
            variant="secondary"
            onClick={() => {
              setIsOpen(false);
              setPromoCode({});
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            form={PROMO_CODE_FORM_ID}
            disabled={isSaving || !submittable}
          >
            Save
          </Button>
        </>
      }
    >
      <form id={PROMO_CODE_FORM_ID}
            className="ticket-drawer"
            onSubmit={
              (e) => {
                e.preventDefault();

                if(isSavingRef.current || !submittable){
                  return false;
                }

                isSavingRef.current = true;
                setIsSaving(true);

                const save = onSubmitPromoCode
                  ? Promise.resolve(onSubmitPromoCode(promoCode))
                  : (promoCode.id ? updatePromoCode : createPromoCode)(csrfToken, team, confirm, promoCode, onClose);
                save.finally(() => {
                  isSavingRef.current = false;
                  setIsSaving(false);
                });
              }
            }>
        <TextInput
          as={MaskedInput}
          id="promo-code-name"
          label={<RequiredLabel>Code Name</RequiredLabel>}
          style={{"textTransform": "uppercase"}}
          mask={promoCodeMask()}
          guide={false}
          error={nameError ? nameError(promoCode) || undefined : undefined}
          hint={nameHint}
          value={promoCode.name || ""}
          onChange={(e) => {
            setPromoCode({...promoCode, name: e.target.value.toUpperCase()});
          }}
        />
        {!discountOnly ? (
        <div className="ticket-drawer-field">
          <span className="ticket-drawer-field-label mb-0">
            <RequiredLabel>What does this code do?</RequiredLabel>
          </span>
          <p className="ticket-drawer-muted-text mb-0">Choose one or both.</p>
          <CheckboxGroup
            name="promo-code-type"
            items={CODE_TYPE_ITEMS}
            values={[
              promoCode.reveal_hidden_tickets && "reveal",
              promoCode.applies_discount && "discount",
            ].filter(Boolean)}
            onChange={(values) => {
              const reveals = values.includes("reveal");
              const discounts = values.includes("discount");
              var updated = { ...promoCode };

              if(reveals !== Boolean(promoCode.reveal_hidden_tickets)) {
                updated = {
                  ...updated,
                  reveal_hidden_tickets: reveals,
                  reveal_all_hidden_tickets: false,
                  ticket_type_ids: [],
                  event_hold_ids: [],
                  general_admission_hold_ids: []
                };

                if(reveals) {
                  updated.applies_to_ticket_types = true;
                  updated.applies_to_add_ons = false;
                  updated.limit_to_one_add_on_per_order = false;
                }
              }

              if(discounts !== Boolean(promoCode.applies_discount)) {
                updated.applies_discount = discounts;

                if(!discounts) {
                  updated = {
                    ...updated,
                    amount_two_decimals: null,
                    percentage: null,
                    applies_to_entire_order: false,
                    discounts_only_selected_ticket_types: false,
                    discounted_ticket_type_ids: [],
                    limit_to_one_ticket_per_order: reveals && Boolean(promoCode.limit_to_one_ticket_per_order),
                    discounted_tickets_per_order: null,
                    limit_to_one_add_on_per_order: false
                  };
                }
              }

              setPromoCode(updated);
            }}
          />
          {!promoCode.reveal_hidden_tickets && !promoCode.applies_discount ? (
            <p className="ticket-drawer-muted-text mb-0" style={errorTextStyle}>Choose at least one.</p>
          ) : null}
        </div>
        ) : null}
        {promoCode.reveal_hidden_tickets ? (
          <div className="ticket-drawer-field">
            <SectionTitle icon={LockUnlocked01} hint="Which tickets this code unlocks.">Hidden tickets</SectionTitle>
            <RadioGroup
              name="promo-code-reveal-scope"
              value={promoCode.reveal_all_hidden_tickets ? "all" : "specific"}
              onChange={(scope) => {
                setPromoCode({
                  ...promoCode,
                  reveal_all_hidden_tickets: scope === "all",
                  ticket_type_ids: [],
                  event_hold_ids: [],
                  general_admission_hold_ids: []
                });
              }}
            >
              <Radio id="promo-code-reveal-specific" value="specific" label="Specific tickets or holds" />
              <Radio id="promo-code-reveal-all-hidden-tickets" value="all" label="All hidden tickets on this event" />
            </RadioGroup>
            {promoCode.reveal_all_hidden_tickets ? (
              <p className="ticket-drawer-muted-text mb-0" style={warningTextStyle}>
                While this code is active it hides every ticket on this event from the
                public, and unlocks every hidden ticket for anyone who enters the code.
                Tickets go back to their normal visibility as soon as the code&rsquo;s
                end date passes.
              </p>
            ) : (
              <>
                <Select
                  id="promo-code-ticket-type-ids"
                  aria-label="Tickets to reveal"
                  placeholder="Select ticket(s)"
                  multiple
                  searchable
                  clearable
                  options={ticketTypeOptions}
                  value={promoCode.ticket_type_ids || []}
                  onChange={handleIdsChange("ticket_type_ids")}
                />
                <Select
                  id="promo-code-event-hold-ids"
                  aria-label="Holds to reveal"
                  placeholder="Select hold(s)"
                  multiple
                  searchable
                  clearable
                  options={holdOptions}
                  value={selectedHoldValues}
                  onChange={handleHoldsChange}
                />
              </>
            )}
            {!promoCode.applies_discount ? (
              <>
                <Checkbox
                  id="promo-code-limit-one-ticket-per-order"
                  label="Limit to one use per order"
                  checked={promoCode.limit_to_one_ticket_per_order || false}
                  onChange={e => {
                    setPromoCode({...promoCode, limit_to_one_ticket_per_order: e.target.checked});
                  }}
                />
                <p className="ticket-drawer-muted-text mb-0">
                  Each order uses the code once, however many tickets it has.
                </p>
              </>
            ) : null}
          </div>
        ) : null}
        {promoCode.applies_discount ? (
          <div className="ticket-drawer-field">
            <SectionTitle icon={Tag01} hint="How much is taken off, and on what.">Discount</SectionTitle>
            <span className="ticket-drawer-field-label mb-0">
              <RequiredLabel>Discount</RequiredLabel>
            </span>
            <div className="ticket-drawer-field-row" style={{alignItems: "center"}}>
              <Tabs
                id="promo-code-discount-mode"
                variant="button-border"
                size="sm"
                items={DISCOUNT_MODE_ITEMS}
                value={promoCode.discount_mode}
                onChange={(mode) => {
                  setPromoCode({
                    ...promoCode,
                    discount_mode: mode,
                    amount_two_decimals: null,
                    percentage: null,
                    applies_to_entire_order: false
                  });
                }}
              />
              <div className="ticket-drawer-field">
                {promoCode.discount_mode === "amount" ? (
                  <TextInput
                    as={CurrencyInput}
                    id="promo-code-amount"
                    aria-label="Discount Amount"
                    leadingText="$"
                    maskOptions={{
                      prefix: "",
                      integerLimit: null
                    }}
                    value={promoCode.amount_two_decimals || ""}
                    onChange={(e) => {
                      setPromoCode({...promoCode, amount_two_decimals: e.target.value});
                    }}
                  />
                ) : (
                  <TextInput
                    as={MaskedInput}
                    id="promo-code-percentage"
                    aria-label="Discount Percentage"
                    trailingText="%"
                    mask={createNumberMask({
                      prefix: "",
                      decimalLimit: 0
                    })}
                    error={parseFloatFromString(promoCode.percentage || "") > 100 ? "Must be 100 or less" : undefined}
                    onChange={(e) => {
                      setPromoCode({...promoCode, percentage: e.target.value});
                    }}
                    value={typeof(promoCode.percentage) !== "undefined" && promoCode.percentage !== null ? promoCode.percentage : ""}
                  />
                )}
              </div>
            </div>
            {promoCode.discount_mode === "amount" && !discountOnly ? (
              <>
                <Checkbox
                  id="promo-code-applies-to-entire-order"
                  label="Apply amount to entire order"
                  checked={promoCode.applies_to_entire_order || false}
                  onChange={e => {
                    setPromoCode({
                      ...promoCode,
                      applies_to_entire_order: e.target.checked,
                      limit_to_one_ticket_per_order: (e.target.checked ? false : promoCode.limit_to_one_ticket_per_order),
                      limit_to_one_add_on_per_order: (e.target.checked ? false : promoCode.limit_to_one_add_on_per_order)
                    });
                  }}
                />
                <p className="ticket-drawer-muted-text mb-0">
                  {promoCode.applies_to_entire_order ? (
                    "The discount amount is taken once off the whole order and split across its eligible items. Fees are still charged on each discounted item."
                  ) : (
                    "The discount amount is taken off each eligible item."
                  )}
                </p>
              </>
            ) : null}
            {!discountOnly ? (
            <>
            <span className="ticket-drawer-field-label mb-0">Discount applies to</span>
            <Checkbox
              id="promo-code-applies-to-ticket-types"
              label="Tickets"
              disabled={promoCode.reveal_hidden_tickets}
              checked={promoCode.applies_to_ticket_types || false}
              onChange={
                (e) => {
                  var updated = {
                    ...promoCode,
                    applies_to_ticket_types: e.target.checked
                  }

                  if(e.target.checked) {
                    updated.limit_to_one_add_on_per_order = false;
                  } else {
                    updated.discounts_only_selected_ticket_types = false;
                    updated.discounted_ticket_type_ids = [];
                    updated.limit_to_one_ticket_per_order = false;
                  }

                  setPromoCode(updated);
                }
              }
            />
            {promoCode.applies_to_ticket_types ? (
              <div className="ticket-drawer-field" style={nestedStyle}>
                <Checkbox
                  id="promo-code-discounts-only-selected-ticket-types"
                  label="Only selected tickets"
                  checked={promoCode.discounts_only_selected_ticket_types || false}
                  onChange={e => {
                    setPromoCode({
                      ...promoCode,
                      discounts_only_selected_ticket_types: e.target.checked,
                      discounted_ticket_type_ids: []
                    });
                  }}
                />
                {promoCode.discounts_only_selected_ticket_types ? (
                  <>
                    <Select
                      id="promo-code-discounted-ticket-type-ids"
                      aria-label="Tickets to discount"
                      placeholder="Select ticket(s)"
                      multiple
                      searchable
                      clearable
                      options={ticketTypeOptions}
                      value={promoCode.discounted_ticket_type_ids || []}
                      onChange={handleIdsChange("discounted_ticket_type_ids")}
                    />
                    <p className="ticket-drawer-muted-text mb-0">
                      Other tickets on this event stay at full price.
                    </p>
                  </>
                ) : null}
                <Checkbox
                  id="promo-code-limit-one-ticket-per-order"
                  label="Limit discount to a number of tickets per order"
                  checked={promoCode.limit_to_one_ticket_per_order || false}
                  onChange={e => {
                    var updated = {
                      ...promoCode,
                      limit_to_one_ticket_per_order: e.target.checked
                    }

                    if(e.target.checked) {
                      updated.limit_to_one_add_on_per_order = false;
                      updated.applies_to_add_ons = false;
                      updated.applies_to_entire_order = false;
                    }

                    setPromoCode(updated);
                  }}
                />
                {promoCode.limit_to_one_ticket_per_order ? (
                  <>
                    <TextInput
                      as={MaskedInput}
                      id="promo-code-discounted-tickets-per-order"
                      aria-label="Tickets per order"
                      trailingText="tickets"
                      mask={createNumberMask({ prefix: "", includeThousandsSeparator: false, decimalLimit: 0 })}
                      value={String(promoCode.discounted_tickets_per_order ?? 1)}
                      onChange={(e) => {
                        setPromoCode({...promoCode, discounted_tickets_per_order: e.target.value});
                      }}
                    />
                    <p className="ticket-drawer-muted-text mb-0">
                      More tickets in the same order are full price.
                    </p>
                  </>
                ) : null}
              </div>
            ) : null}
            <Checkbox
              id="promo-code-applies-to-add-ons"
              label="Add-ons"
              disabled={promoCode.reveal_hidden_tickets}
              checked={promoCode.applies_to_add_ons || false}
              onChange={
                (e) => {
                  var updated = {
                    ...promoCode,
                    applies_to_add_ons: e.target.checked
                  };

                  if(e.target.checked) {
                    updated.limit_to_one_ticket_per_order = false;
                  } else {
                    updated.limit_to_one_add_on_per_order = false;
                  }

                  setPromoCode(updated);
                }
              }
            />
            {promoCode.applies_to_add_ons && !promoCode.reveal_hidden_tickets ? (
              <div style={nestedStyle}>
                <Checkbox
                  id="promo-code-limit-one-add-on-per-order"
                  label="Limit discount to one add-on per order"
                  checked={promoCode.limit_to_one_add_on_per_order || false}
                  onChange={e => {
                    var updated = {
                      ...promoCode,
                      limit_to_one_add_on_per_order: e.target.checked
                    }

                    if(e.target.checked) {
                      updated.applies_to_ticket_types = false;
                      updated.discounts_only_selected_ticket_types = false;
                      updated.discounted_ticket_type_ids = [];
                      updated.limit_to_one_ticket_per_order = false;
                      updated.applies_to_entire_order = false;
                    }

                    setPromoCode(updated);
                  }}
                />
              </div>
            ) : null}
            </>
            ) : null}
          </div>
        ) : null}
        <div className="ticket-drawer-field">
          <SectionTitle hint="How many times this code can be redeemed across all orders.">Usage</SectionTitle>
          <RadioGroup
            name="promo-code-usage"
            value={promoCode.unlimited ? "unlimited" : "limited"}
            onChange={(usage) => {
              setPromoCode({
                ...promoCode,
                unlimited: usage === "unlimited",
                limit: usage === "unlimited" ? null : promoCode.limit
              });
            }}
          >
            <Radio id="promo-code-unlimited" value="unlimited" label="Unlimited" />
            <Radio id="promo-code-limited" value="limited" label="Limited" />
          </RadioGroup>
        </div>
        {!promoCode.unlimited ? (
          <TextInput
            as={MaskedInput}
            id="promo-code-available"
            label={<RequiredLabel>Available Quantity</RequiredLabel>}
            mask={createNumberMask({
              prefix: "",
              decimalLimit: 0
            })}
            onChange={
              (e) => {
                setPromoCode({...promoCode, limit: e.target.value});
              }
            }
            value={promoCode.limit || ""}
          />
        ) : null}
        <TextInput
          as={MaskedInput}
          id="promo-code-uses-per-fan"
          label="Uses per fan"
          trailingText="orders"
          mask={createNumberMask({ prefix: "", includeThousandsSeparator: false, decimalLimit: 0 })}
          value={promoCode.uses_per_fan ? String(promoCode.uses_per_fan) : ""}
          onChange={(e) => {
            setPromoCode({...promoCode, uses_per_fan: e.target.value});
          }}
          hint="How many orders one fan (one email) can use this code on. Leave blank for no limit."
        />
        {renderWindow ? renderWindow(promoCode, setPromoCode) : (
        <>
        {(availabilityOptions || AVAILABILITY_OPTIONS).length > 1 ? (
        <Select
          id="promo-code-availability"
          label="When are promo codes available?"
          options={availabilityOptions || AVAILABILITY_OPTIONS}
          value={promoCode.availability || "Based on show details"}
          onChange={(availability) => {
            setPromoCode({...promoCode, availability: availability});
          }}
        />
        ) : null}
        {promoCode.availability === "Date & Time" ? (
          <>
            <div className="ticket-drawer-field-row">
              <div className="ticket-drawer-field">
                <label htmlFor="promo-code-start-date" className="ticket-drawer-field-label">
                  <RequiredLabel>Start Date</RequiredLabel>
                </label>
                <Flatpickr
                  id="promo-code-start-date"
                  options={{
                    dateFormat: "M j, Y"
                  }}
                  placeholder={"Select date"}
                  className={"form-control ticketing-date-picker form-control-flatpickr"}
                  value={promoCode.start_date_midday}
                  onChange={dates => {
                    var startDate = parseDateTime(dates[0]);
                    var startTime = parseDateTime(promoCode.start_time_with_wrong_date);
                    var endDate   = parseDateTime(promoCode.end_date_midday);
                    var endTime   = parseDateTime(promoCode.end_time_with_wrong_date);

                    var attrs = {
                      start_date_midday: startDate
                    };

                    if(endDate && endDate < startDate){
                      endDate = "";
                      attrs = Object.assign({}, attrs, {
                        end_date_midday: endDate
                      });
                    }

                    if(startDate && startTime && endDate && endTime){
                      var startTimeCorrectDate = timeOnDate(startDate, startTime);
                      var endTimeCorrectDate = timeOnDate(endDate, endTime);

                      if(endTimeCorrectDate < startTimeCorrectDate){
                        attrs = Object.assign({}, attrs, {end_time_with_wrong_date: ""});
                      }
                    }

                    setPromoCode(Object.assign({}, promoCode, attrs));
                  }}
                />
              </div>
              <div className="ticket-drawer-field">
                <TimeField
                  id="promo-code-start-time"
                  label={<RequiredLabel>Start Time</RequiredLabel>}
                  placeholder="Select time"
                  value={toTimeValue(promoCode.start_time_with_wrong_date)}
                  onChange={(value) => {
                    var startDate = parseDateTime(promoCode.start_date_midday);
                    var startTime = fromTimeValue(value);
                    var endDate   = parseDateTime(promoCode.end_date_midday);
                    var endTime   = parseDateTime(promoCode.end_time_with_wrong_date);

                    var attrs = {
                      start_time_with_wrong_date: startTime
                    };

                    if(startDate && startTime && endDate && endTime){
                      var startTimeCorrectDate = timeOnDate(startDate, startTime);
                      var endTimeCorrectDate = timeOnDate(endDate, endTime);

                      if(endTimeCorrectDate < startTimeCorrectDate){
                        attrs = Object.assign({}, attrs, {end_time_with_wrong_date: ""});
                      }
                    }

                    setPromoCode(Object.assign({}, promoCode, attrs));
                  }}
                />
              </div>
            </div>
            <div className="ticket-drawer-field-row">
              <div className="ticket-drawer-field">
                <label htmlFor="promo-code-end-date" className="ticket-drawer-field-label">
                  <RequiredLabel>End Date</RequiredLabel>
                </label>
                <Flatpickr
                  id="promo-code-end-date"
                  options={{
                    dateFormat: "M j, Y",
                    minDate: (promoCode.start_date_midday ? (
                      parseDateTime(promoCode.start_date_midday).setHours(0,0,0,0)
                    ) : null)
                  }}
                  disabled={!promoCode.start_date_midday}
                  placeholder={"Select date"}
                  className={"form-control ticketing-date-picker form-control-flatpickr " + (!promoCode.start_date_midday ? "form-control-disabled" : "")}
                  value={promoCode.end_date_midday}
                  onChange={dates => {
                    var startDate = parseDateTime(promoCode.start_date_midday);
                    var startTime = parseDateTime(promoCode.start_time_with_wrong_date);
                    var endDate   = parseDateTime(dates[0]);
                    var endTime   = parseDateTime(promoCode.end_time_with_wrong_date);

                    var attrs = {
                      end_date_midday: endDate
                    };

                    if(startDate && startTime && endTime){
                      var startTimeCorrectDate = timeOnDate(startDate, startTime);
                      var endTimeCorrectDate = timeOnDate(endDate, endTime);

                      if(endTimeCorrectDate < startTimeCorrectDate){
                        attrs = Object.assign({}, attrs, {end_time_with_wrong_date: ""});
                      }
                    }

                    setPromoCode(Object.assign({}, promoCode, attrs));
                  }}
                />
              </div>
              <div className="ticket-drawer-field">
                <TimeField
                  id="promo-code-end-time"
                  label={<RequiredLabel>End Time</RequiredLabel>}
                  placeholder="Select time"
                  disabled={!promoCode.end_date_midday || !promoCode.start_time_with_wrong_date}
                  min={
                    sameDay(promoCode.start_date_midday, promoCode.end_date_midday)
                      ? toTimeValue(promoCode.start_time_with_wrong_date)
                      : undefined
                  }
                  value={toTimeValue(promoCode.end_time_with_wrong_date)}
                  onChange={(value) => {
                    setPromoCode({...promoCode, end_time_with_wrong_date: fromTimeValue(value)});
                  }}
                />
              </div>
            </div>
          </>
        ) : (
          <>
            <Select
              id="promo-code-start-show-activity"
              label={<RequiredLabel>Start Show Detail</RequiredLabel>}
              placeholder="Select a show detail"
              options={showActivityOptions}
              value={promoCode.start_show_activity_id || null}
              onChange={showActivityId => {
                setPromoCode({...promoCode, start_show_activity_id: showActivityId});
              }}
            />
            <Select
              id="promo-code-end-show-activity"
              label={<RequiredLabel>End Show Detail</RequiredLabel>}
              placeholder="Select a show detail"
              options={showActivityOptions}
              value={promoCode.end_show_activity_id || null}
              onChange={showActivityId => {
                setPromoCode({...promoCode, end_show_activity_id: showActivityId});
              }}
            />
          </>
        )}
        </>
        )}
        {!discountOnly && (currentUser.admin || currentUser.is_masquerading) && team.id !== 9804 ? (
          <div className="ticket-drawer-field">
            <TextInput
              multiline
              rows={8}
              id="promo-code-custom-js"
              label="Custom Javascript"
              value={promoCode.custom_js || ""}
              onChange={e => {
                setPromoCode({...promoCode, custom_js: e.target.value});
              }}
            />
            <div className="ticket-drawer-muted-text">
              <p className="mt-2">Add custom javascript to determine which ticket types and add-ons are eligible for this promo code.</p>
              <p>
                You must define a function called "customJavascript" that takes one object argument.
                This object argument contains the ticket type and add on IDs and quantities present in the cart.
              </p>
              <p className="mb-0">
                Example:{` `}
              </p>
              <pre style={codeSampleStyle}>
                <code>
                  {customJsArgumentExample()}
                </code>
              </pre>
              <p>The function should return an object to determine how many times the promo code can be applied for each ticket type or add-on.</p>
              <p>
                Include two keys: "ticket_types" and "add_ons". Each key should have a value of an object containing{` `}
                the ticket type or add-on ID as the key and the number of times the promo code should be applied as the value.
              </p>
              <p className="mb-0">
                Example:{` `}
              </p>
              <pre style={codeSampleStyle}>
                <code>
                  {customJsReturnExample()}
                </code>
              </pre>
              <p className="mb-0">
                In this example the promo code would be applied twice to TicketType#1 and once to AddOn#3.
              </p>
            </div>
          </div>
        ) : null}
        {!discountOnly ? (
        <div className="ticket-drawer-field">
          <Checkbox
            id="promo-code-save-as-default"
            label="Save as default"
            checked={promoCode.should_be_default || false}
            onChange={
              (e) => {
                setPromoCode({...promoCode, should_be_default: e.target.checked});
              }
            }
          />
          {promoCode.should_be_default ? (
            <p className="ticket-drawer-muted-text mb-0" style={warningTextStyle}>
              {defaultProblem || "This code will be added to every new event you build for this venue going forward."}
            </p>
          ) : null}
        </div>
        ) : null}
      </form>
    </Drawer>
  );
};

PromoCodeForm.propTypes = {
  currentUser: PropTypes.object.isRequired,
  confirm: PropTypes.object.isRequired,
  csrfToken: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  promoCodeToCopy: PropTypes.object.isRequired,
  promoCodeToEdit: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  showActivities: PropTypes.array.isRequired,
  team: PropTypes.object.isRequired,
  ticketTypes: PropTypes.array.isRequired,
  holds: PropTypes.array,
  fetchHolds: PropTypes.func,
  generalAdmissionHolds: PropTypes.array,
  fetchGeneralAdmissionHolds: PropTypes.func,
  // The series wizard passes these: it seeds the form itself, saves through
  // its own callback and plans windows as show details plus an offset.
  initialPromoCode: PropTypes.object,
  onSubmitPromoCode: PropTypes.func,
  canSubmit: PropTypes.func,
  renderWindow: PropTypes.func,
  nameError: PropTypes.func,
  nameHint: PropTypes.node,
  saveAsDefaultProblem: PropTypes.func,
  discountOnly: PropTypes.bool,
  availabilityOptions: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })),
}

export default PromoCodeForm;
